// src/services/tenantManagementAPI.ts
// ✅ LOGGING: Complete migration to centralized logging system

import { authService } from './authService';
import { logger } from '../utils/logger';
import { configService } from './configService';
import {
  TenantRecord,
  CreateTenantRequest,
  UpdateTenantRequest,
  UpdateTenantStatusRequest,
  TenantAPIResponse,
  ListTenantsResponse,
  GetTenantResponse,
  CreateTenantResponse,
  UpdateTenantResponse,
  TenantLookupResponse,
  TenantListFilters,
  TenantStats
} from '../types/tenant';

// ✅ LOGGING: Create dedicated logger for Tenant Management API
const tenantManagementLogger = logger.createServiceLogger('TenantManagementAPI');

class TenantManagementAPI {
  private baseURL: string;

  constructor() {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    if (!apiUrl) {
      throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }
    this.baseURL = apiUrl;
  }

  /**
   * Make authenticated request using centralized authService
   */
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<TenantAPIResponse<T>> {
    const timer = logger.startTimer('TenantManagementAPI', 'makeRequest');
    
    tenantManagementLogger.info('Making API request', {
      endpoint: `${this.baseURL}${endpoint}`,
      method: options.method || 'GET',
      timestamp: new Date().toISOString()
    });

    try {
      const response = await authService.makeAuthenticatedRequest(
        `${this.baseURL}${endpoint}`,
        options
      );

      tenantManagementLogger.info('API response received', {
        endpoint,
        status: response.status,
        statusText: response.statusText
      });

      if (!response.ok) {
        const responseText = await response.text();
        tenantManagementLogger.error('API request failed', {
          endpoint,
          status: response.status,
          statusText: response.statusText,
          responseText
        });
        
        timer(); // Complete timing
        return {
          success: false,
          error: {
            code: 'HTTP_ERROR',
            message: `HTTP ${response.status}: ${response.statusText} - ${responseText}`
          }
        };
      }

      const data = await response.json();
      tenantManagementLogger.debug('API request successful', {
        endpoint,
        dataKeys: Object.keys(data)
      });
      
      timer(); // Complete timing
      return {
        success: true,
        data: data.data || data
      };
    } catch (error: any) {
      timer(); // Complete timing even on error
      
      tenantManagementLogger.error('API request error', {
        endpoint,
        error: error.message,
        errorCode: error.code
      }, error);
      
      // Handle auth service error types
      if (error.code === 'NO_SESSION') {
        return {
          success: false,
          error: { code: 'AUTH_ERROR', message: 'Please log in again' }
        };
      }

      if (error.code === 'TOKEN_EXPIRED') {
        return {
          success: false,
          error: { code: 'AUTH_ERROR', message: 'Session expired, please refresh' }
        };
      }

      if (error.code === 'NETWORK_ERROR') {
        return {
          success: false,
          error: { code: 'NETWORK_ERROR', message: error.message }
        };
      }

      return {
        success: false,
        error: {
          code: 'API_ERROR',
          message: error.message || 'Unknown error occurred'
        }
      };
    }
  }

  /**
   * Get tenant by slug (PUBLIC endpoint - no authentication required)
   * Used during login flow to resolve slug to tenantId
   */
  async getTenantBySlug(slug: string): Promise<TenantAPIResponse<TenantLookupResponse>> {
    const timer = logger.startTimer('TenantManagementAPI', 'getTenantBySlug');
    
    tenantManagementLogger.info('Looking up tenant by slug', {
      slug,
      timestamp: new Date().toISOString()
    });

    try {
      // This is a public endpoint, so we don't use authService
      const response = await fetch(`${this.baseURL}/tenants/lookup?slug=${encodeURIComponent(slug)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      tenantManagementLogger.info('Tenant lookup response received', {
        slug,
        status: response.status,
        statusText: response.statusText
      });

      if (!response.ok) {
        const responseText = await response.text();
        tenantManagementLogger.error('Tenant lookup failed', {
          slug,
          status: response.status,
          statusText: response.statusText,
          responseText
        });
        
        timer();
        
        // 404 means tenant not found
        if (response.status === 404) {
          return {
            success: false,
            error: {
              code: 'TENANT_NOT_FOUND',
              message: 'Department not found. Please check your department slug and try again.'
            }
          };
        }
        
        return {
          success: false,
          error: {
            code: 'LOOKUP_ERROR',
            message: `Failed to lookup tenant: ${responseText}`
          }
        };
      }

      const data = await response.json();
      tenantManagementLogger.info('Tenant lookup successful', {
        slug,
        tenantId: data.data?.tenantId,
        organizationName: data.data?.tenant?.organizationName
      });
      
      timer();
      return {
        success: true,
        data: data.data
      };
    } catch (error: any) {
      timer();
      
      tenantManagementLogger.error('Tenant lookup error', {
        slug,
        error: error.message
      }, error);
      
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: 'Unable to connect to server. Please check your internet connection.'
        }
      };
    }
  }

  /**
   * List all tenants with optional filtering
   */
  async listTenants(filters: TenantListFilters = {}): Promise<TenantAPIResponse<ListTenantsResponse>> {
    tenantManagementLogger.info('Listing tenants', {
      filters,
      timestamp: new Date().toISOString()
    });
    
    const params = new URLSearchParams();
    
    if (filters.status && filters.status !== 'all') {
      params.append('status', filters.status);
    }
    
    if (filters.limit) {
      params.append('limit', filters.limit.toString());
    }
    
    if (filters.lastEvaluatedKey) {
      params.append('lastEvaluatedKey', filters.lastEvaluatedKey);
    }

    const queryString = params.toString();
    const endpoint = queryString ? `/tenants?${queryString}` : '/tenants';
    
    return this.makeRequest<ListTenantsResponse>(endpoint);
  }

  /**
   * Get specific tenant by ID
   */
  async getTenant(tenantId: string): Promise<TenantAPIResponse<GetTenantResponse>> {
    tenantManagementLogger.info('Getting tenant by ID', {
      tenantId
    });
    return this.makeRequest<GetTenantResponse>(`/tenants/${tenantId}`);
  }

  /**
   * Create new tenant
   */
  async createTenant(tenantData: CreateTenantRequest): Promise<TenantAPIResponse<CreateTenantResponse>> {
    tenantManagementLogger.info('Creating new tenant', {
      organizationName: tenantData.organizationName,
      tenantSlug: tenantData.tenantSlug,
      contactEmail: tenantData.contactEmail,
      organizationType: tenantData.organizationType,
      subscriptionTier: tenantData.subscriptionTier
    });
    
    return this.makeRequest<CreateTenantResponse>('/tenants', {
      method: 'POST',
      body: JSON.stringify({ tenant: tenantData })
    });
  }

  /**
   * Update existing tenant
   */
  async updateTenant(
    tenantId: string,
    tenantData: UpdateTenantRequest
  ): Promise<TenantAPIResponse<UpdateTenantResponse>> {
    tenantManagementLogger.info('Updating tenant', {
      tenantId,
      updateFields: Object.keys(tenantData)
    });
    
    return this.makeRequest<UpdateTenantResponse>(`/tenants/${tenantId}`, {
      method: 'PUT',
      body: JSON.stringify({ tenant: tenantData })
    });
  }

  /**
   * Update tenant status (activate, deactivate, etc.)
   */
  async updateTenantStatus(
    tenantId: string,
    statusUpdate: UpdateTenantStatusRequest
  ): Promise<TenantAPIResponse<UpdateTenantResponse>> {
    tenantManagementLogger.info('Updating tenant status', {
      tenantId,
      newStatus: statusUpdate.status,
      reason: statusUpdate.reason
    });
    
    return this.makeRequest<UpdateTenantResponse>(`/tenants/${tenantId}/status`, {
      method: 'PATCH',
      body: JSON.stringify(statusUpdate)
    });
  }

  /**
   * Get tenant statistics (for dashboard)
   */
  async getTenantStats(): Promise<TenantAPIResponse<TenantStats>> {
    const timer = logger.startTimer('TenantManagementAPI', 'getTenantStats');
    tenantManagementLogger.info('Getting tenant statistics');
    
    // For now, calculate from list of tenants
    // In future, could be a dedicated endpoint
    const response = await this.listTenants({ limit: 100 });
    
    if (!response.success || !response.data) {
      timer(); // Complete timing
      return {
        success: false,
        error: response.error || { code: 'STATS_ERROR', message: 'Failed to get tenant stats' }
      };
    }

    const tenants = response.data.tenants;
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const stats: TenantStats = {
      totalTenants: tenants.length,
      activeTenants: tenants.filter(t => t.status === 'ACTIVE').length,
      trialTenants: tenants.filter(t => t.status === 'TRIAL').length,
      inactiveTenants: tenants.filter(t => t.status === 'INACTIVE' || t.status === 'SUSPENDED').length,
      bySubscriptionTier: {
        BASIC: tenants.filter(t => t.subscriptionTier === 'BASIC').length,
        PREMIUM: tenants.filter(t => t.subscriptionTier === 'PREMIUM').length,
        ENTERPRISE: tenants.filter(t => t.subscriptionTier === 'ENTERPRISE').length
      },
      byOrganizationType: {
        POLICE_DEPARTMENT: tenants.filter(t => t.organizationType === 'POLICE_DEPARTMENT').length,
        SHERIFF_OFFICE: tenants.filter(t => t.organizationType === 'SHERIFF_OFFICE').length,
        STATE_POLICE: tenants.filter(t => t.organizationType === 'STATE_POLICE').length,
        FEDERAL_AGENCY: tenants.filter(t => t.organizationType === 'FEDERAL_AGENCY').length,
        OTHER: tenants.filter(t => t.organizationType === 'OTHER').length
      },
      recentlyCreated: tenants.filter(t => new Date(t.createdAt) > thirtyDaysAgo).length
    };

    tenantManagementLogger.info('Tenant statistics calculated', {
      totalTenants: stats.totalTenants,
      activeTenants: stats.activeTenants,
      trialTenants: stats.trialTenants,
      inactiveTenants: stats.inactiveTenants,
      recentlyCreated: stats.recentlyCreated
    });

    timer(); // Complete timing
    return {
      success: true,
      data: stats
    };
  }

  /**
   * Search tenants by name or email
   */
  async searchTenants(query: string): Promise<TenantAPIResponse<ListTenantsResponse>> {
    tenantManagementLogger.info('Searching tenants', {
      query,
      timestamp: new Date().toISOString()
    });
    
    // Get all tenants and filter client-side for now
    // In future, could be a server-side search endpoint
    const response = await this.listTenants({ limit: 100 });
    
    if (!response.success || !response.data) {
      return response;
    }

    const searchTerm = query.toLowerCase();
    const filteredTenants = response.data.tenants.filter(tenant =>
      tenant.organizationName.toLowerCase().includes(searchTerm) ||
      tenant.contactEmail.toLowerCase().includes(searchTerm) ||
      tenant.tenantId.toLowerCase().includes(searchTerm) ||
      (tenant.tenantSlug && tenant.tenantSlug.toLowerCase().includes(searchTerm)) ||  // ADD NULL CHECK
      (tenant.city && tenant.city.toLowerCase().includes(searchTerm)) ||
      (tenant.state && tenant.state.toLowerCase().includes(searchTerm))
    );

    tenantManagementLogger.info('Tenant search completed', {
      query,
      totalTenants: response.data.tenants.length,
      matchingTenants: filteredTenants.length
    });

    return {
      success: true,
      data: {
        tenants: filteredTenants,
        count: filteredTenants.length,
        scannedCount: response.data.scannedCount,
        lastEvaluatedKey: undefined // Reset pagination for search results
      }
    };
  }

  /**
   * Validate tenant slug format
   */
  validateTenantSlug(slug: string): string[] {
    const errors: string[] = [];

    if (!slug) {
      return errors; // Slug is optional, so no error if empty
    }

    // Length check
    if (slug.length < 3 || slug.length > 50) {
      errors.push('Tenant slug must be between 3 and 50 characters');
    }

    // Format check: lowercase letters, numbers, and hyphens only
    const slugRegex = /^[a-z0-9]+(-[a-z0-9]+)*$/;
    if (!slugRegex.test(slug)) {
      errors.push('Tenant slug must contain only lowercase letters, numbers, and hyphens. Must start and end with a letter or number.');
    }

    // Check if starts/ends with hyphen
    if (slug.startsWith('-') || slug.endsWith('-')) {
      errors.push('Tenant slug cannot start or end with a hyphen');
    }

    // Check for consecutive hyphens
    if (slug.includes('--')) {
      errors.push('Tenant slug cannot contain consecutive hyphens');
    }

    return errors;
  }

  /**
   * Generate slug suggestion from organization name
   */
  generateSlugSuggestion(organizationName: string): string {
    return organizationName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  }

  /**
   * Validate tenant data before submission
   */
  validateTenantData(tenantData: CreateTenantRequest | UpdateTenantRequest): string[] {
    tenantManagementLogger.debug('Validating tenant data', {
      dataKeys: Object.keys(tenantData)
    });

    const errors: string[] = [];

    // Required fields for creation
    if ('organizationName' in tenantData && !tenantData.organizationName?.trim()) {
      errors.push('Organization name is required');
    }

    if ('contactEmail' in tenantData && !tenantData.contactEmail?.trim()) {
      errors.push('Contact email is required');
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (tenantData.contactEmail && !emailRegex.test(tenantData.contactEmail)) {
      errors.push('Invalid contact email format');
    }

    if (tenantData.billingEmail && !emailRegex.test(tenantData.billingEmail)) {
      errors.push('Invalid billing email format');
    }

    // Slug validation
    if (tenantData.tenantSlug) {
      const slugErrors = this.validateTenantSlug(tenantData.tenantSlug);
      errors.push(...slugErrors);
    }

    // Numeric validations
    if (tenantData.maxUsers && (tenantData.maxUsers < 1 || tenantData.maxUsers > 10000)) {
      errors.push('Max users must be between 1 and 10,000');
    }

    if (tenantData.maxInventoryItems && (tenantData.maxInventoryItems < 1 || tenantData.maxInventoryItems > 1000000)) {
      errors.push('Max inventory items must be between 1 and 1,000,000');
    }

    if (tenantData.maxStorageGB && (tenantData.maxStorageGB < 1 || tenantData.maxStorageGB > 1000)) {
      errors.push('Max storage must be between 1 and 1,000 GB');
    }

    if (errors.length > 0) {
      tenantManagementLogger.warn('Tenant data validation failed', {
        errors,
        dataKeys: Object.keys(tenantData)
      });
    } else {
      tenantManagementLogger.debug('Tenant data validation passed');
    }

    return errors;
  }
}

// Export singleton instance
export const tenantManagementAPI = new TenantManagementAPI();
